/**
 * BILLING-S1B — humaniza `limits` do plano (vindas do JSON do backend)
 * em bullets legíveis pra renderizar nos PlanCards.
 *
 * Cada item retorna { text, included }:
 *   included=true  → ícone ✓ verde
 *   included=false → ícone X cinza (feature do tier acima)
 *
 * Trade-off de design: não mostrar -1 cru ("Tarefas: -1") nem chave
 * snake_case ("voice_seconds_per_month: 14400"). User lê "Tarefas
 * ilimitadas", "4 horas de voz por mês".
 *
 * Cores/style ficam pra PlanCard.jsx — esta função é puro JSON-in,
 * JSON-out pra ser smoke-testável sem React.
 */

const ESS_KEYS = "essencial";  // não usado direto; helper deixou pra clarity

function _formatVoice(seconds) {
  if (typeof seconds !== "number" || seconds <= 0) return null;
  if (seconds <= 60 * 60) {
    // ate 1h, em minutos
    return `${Math.round(seconds / 60)} min de voz por mês`;
  }
  // acima de 1h, em horas
  const hours = Math.round((seconds / 3600) * 10) / 10;
  const hoursStr = Number.isInteger(hours) ? String(hours) : hours.toFixed(1);
  return `${hoursStr} ${hours === 1 ? "hora" : "horas"} de voz por mês`;
}

function _formatTasksNotesReminders(limit) {
  if (limit === -1) return "Tarefas, notas e lembretes ilimitados";
  return `Até ${limit} tarefas, notas e lembretes por mês`;
}

function _formatProjects(limit) {
  if (limit === -1) return "Projetos ilimitados";
  return `${limit} projeto${limit === 1 ? "" : "s"} ativo${limit === 1 ? "" : "s"}`;
}

function _formatAiMessages(perDay, overageEnabled) {
  if (!perDay) return null;
  if (overageEnabled) {
    return `${perDay} conversas com Nexo por dia (overage R$ 0,30 acima)`;
  }
  return `${perDay} conversas com Nexo por dia`;
}

function _formatTtsVoices(allowed) {
  if (!Array.isArray(allowed) || allowed.length === 0) return null;
  if (allowed.length === 1) return `Voz ${allowed[0].charAt(0).toUpperCase()}${allowed[0].slice(1)}`;
  if (allowed.length >= 6) return "Todas as 6 vozes TTS";
  return `${allowed.length} vozes TTS`;
}

function _formatGcalWrite(write) {
  return write
    ? { text: "Google Calendar bidirecional", included: true }
    : { text: "Google Calendar (apenas leitura)", included: true };
}

function _formatBriefing(autoOn) {
  return autoOn
    ? { text: "Briefing 360 automático diário", included: true }
    : { text: "Briefing 360 sob demanda", included: true };
}

function _formatBackup(freq) {
  if (freq === "daily") return { text: "Backup diário", included: true };
  if (freq === "weekly") return { text: "Backup semanal", included: true };
  return { text: "Backup não incluso", included: false };
}

function _formatFocusMode(on) {
  return on
    ? { text: "Modo Foco", included: true }
    : { text: "Modo Foco", included: false };
}

/**
 * Recebe o objeto `limits` do plan + features[] do JSON.
 * Retorna lista de { text, included } pra renderizar.
 *
 * Ordem: do mais importante (volume + IA) pro auxiliar (backup).
 */
export function humanizePlanFeatures(plan) {
  if (!plan || typeof plan !== "object") return [];
  const limits = plan.limits || {};
  const out = [];

  const tnrText = _formatTasksNotesReminders(limits.tasks_notes_reminders_per_month);
  if (tnrText) out.push({ text: tnrText, included: true });

  const projText = _formatProjects(limits.projects_active);
  if (projText) out.push({ text: projText, included: true });

  const aiText = _formatAiMessages(limits.ai_messages_per_day, limits.overage_enabled);
  if (aiText) out.push({ text: aiText, included: true });

  const voiceText = _formatVoice(limits.voice_seconds_per_month);
  if (voiceText) out.push({ text: voiceText, included: true });

  const voicesText = _formatTtsVoices(limits.tts_voices_allowed);
  if (voicesText) out.push({ text: voicesText, included: true });

  out.push(_formatGcalWrite(!!limits.gcal_write));
  out.push(_formatBriefing(!!limits.briefing_360_auto));
  out.push(_formatFocusMode(!!limits.focus_mode));
  out.push(_formatBackup(limits.backup_frequency));

  return out;
}

// ===================== PRICE FORMATTING =====================

/**
 * Formata preço em BRL: 49.90 → "R$ 49,90".
 * tabular-nums + locale pt-BR pra centavos sempre 2 dígitos.
 */
export function formatBrlPrice(value) {
  if (typeof value !== "number" || Number.isNaN(value)) return "R$ —";
  return `R$ ${value.toFixed(2).replace(".", ",")}`;
}

/**
 * Subtitle por slug — copy fixa, não vinda do JSON.
 * Se slug desconhecido (interno tipo pro_trial), retorna "" e PlanCard
 * fallback usa features[0].
 */
export const PLAN_SUBTITLE = {
  essencial: "Pra experimentar sem compromisso",
  pro: "Pra uso diário regular",
  premium: "Pra power users e empreendedores",
};

/**
 * Border-left color por tier. Hex pra style inline (sem dep Tailwind class).
 */
export const PLAN_TIER_COLOR = {
  essencial: "#06b6d4",  // cyan
  pro: "#3b82f6",        // blue
  premium: "#a855f7",    // purple
};

/**
 * CTA label por slug. Se desconhecido cai em "Escolher plano".
 */
export function ctaLabelFor(plan) {
  if (!plan || !plan.name) return "Escolher plano";
  return `Escolher ${plan.name}`;
}
